import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Descriptions, Empty, Progress, Table, Tag } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Badge, Button, Card } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import useAccess, { MODULES } from "../../hooks/useAccess";
import SearchSelect from "../../components/SearchSelect";
import WebhooksManager from "./components/WebhooksManager";

const badgeForStatus = (status) => {
  switch ((status || "").toLowerCase()) {
    case "paid":
      return "success";
    case "pending":
      return "warning";
    case "failed":
    case "chargeback":
      return "error";
    case "cancelled":
    case "refunded":
      return "neutral";
    default:
      return "info";
  }
};

/**
 * One rolling-cycle allowance bar. Weekly and monthly are shown side by side because either
 * can be the binding constraint, and the weekly one is usually the surprise.
 */
const CycleBar = ({ label, used, limit, resetUtc }) => {
  const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div style={{ flex: 1, minWidth: 220 }}>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
        {label} — {Number(used || 0).toLocaleString()} / {Number(limit || 0).toLocaleString()}
      </div>
      <Progress
        percent={percent}
        strokeColor={
          percent >= 90
            ? "var(--color-error)"
            : percent >= 70
            ? "var(--color-warning)"
            : "var(--color-primary)"
        }
      />
      {resetUtc && (
        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
          Resets {new Date(resetUtc).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

const SubscriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [subscriptionId, setSubscriptionId] = useState(id ? Number(id) : null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const { mutate } = useMutations();
  const { can } = useAccess();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  // /payments/detail is reachable with the Subscriptions grant that already guards this
  // route, so the figures themselves need no extra check. Webhooks are a module of their own.
  const canReadWebhooks = can(MODULES.WEBHOOKS);

  useEffect(() => {
    if (!subscriptionId) return;

    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      await mutateRef.current("fetchSubscriptionDetail", {
        method: "get",
        endpoint: `/payments/detail/${subscriptionId}`,
        onSuccess: (res) => {
          if (!cancelled) setDetail(res);
        },
        onError: () => {
          if (!cancelled) setDetail(null);
        },
      });
      if (!cancelled) setLoading(false);
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, [subscriptionId]);

  const tokenColumns = [
    {
      title: "Period",
      key: "period",
      render: (_, row) => (
        <span>
          {new Date(row.fromDate).toLocaleDateString()} -{" "}
          {new Date(row.toDate).toLocaleDateString()}
          {row.isCurrent && (
            <Badge variant="success" style={{ marginLeft: 8 }}>
              Active
            </Badge>
          )}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => <Badge variant={badgeForStatus(value)}>{value}</Badge>,
    },
    {
      title: "Token Used",
      dataIndex: "tokenUsed",
      key: "tokenUsed",
    },
    {
      title: "Token Limit",
      dataIndex: "tokenLimit",
      key: "tokenLimit",
    },
    {
      title: "Remaining",
      dataIndex: "tokenRemaining",
      key: "tokenRemaining",
      render: (value) => (
        <span style={{ color: value === 0 ? "var(--color-error)" : "inherit" }}>
          {value}
        </span>
      ),
    },
    {
      title: "Usage",
      key: "usage",
      width: 200,
      render: (_, row) => {
        const pct =
          row.tokenLimit > 0 ? Math.round((row.tokenUsed / row.tokenLimit) * 100) : 0;
        return (
          <Progress
            percent={pct}
            size="small"
            strokeColor={
              pct >= 90
                ? "var(--color-error)"
                : pct >= 70
                ? "var(--color-warning)"
                : "var(--color-primary)"
            }
          />
        );
      },
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (value, row) =>
        `${Number(value || 0).toFixed(2)} ${row.currencyCode || detail?.currencyCode || "EUR"}`,
    },
    {
      title: "Top-Ups",
      key: "topUps",
      render: (_, row) =>
        row.topUps && row.topUps.length > 0 ? (
          <span>
            {row.topUps.length} top-up{row.topUps.length > 1 ? "s" : ""} (+
            {row.topUps.reduce((sum, topUp) => sum + (topUp.tokenAllowance || 0), 0)} tokens)
          </span>
        ) : (
          "-"
        ),
    },
  ];

  const expandedRowRender = (record) => {
    if (!record.topUps || record.topUps.length === 0) return null;

    const topUpColumns = [
      { title: "Payment #", dataIndex: "paymentId", key: "paymentId" },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (value) => <Badge variant={badgeForStatus(value)}>{value}</Badge>,
      },
      {
        title: "Total",
        dataIndex: "totalAmount",
        key: "totalAmount",
        render: (value, row) =>
          `${Number(value || 0).toFixed(2)} ${row.currencyCode || detail?.currencyCode || "EUR"}`,
      },
      {
        title: "Date",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (value) => new Date(value).toLocaleDateString(),
      },
      {
        title: "Credit Granted",
        dataIndex: "tokenAllowance",
        key: "tokenAllowance",
        render: (value, row) =>
          `${Number(value || 0).toLocaleString()}${row.units > 1 ? ` (${row.units} packs)` : ""}`,
      },
      {
        title: "Spent",
        dataIndex: "tokensConsumed",
        key: "tokensConsumed",
        render: (value) => Number(value || 0).toLocaleString(),
      },
      {
        title: "Remaining",
        dataIndex: "tokensRemaining",
        key: "tokensRemaining",
        render: (value) => (
          <strong>{Number(value || 0).toLocaleString()}</strong>
        ),
      },
      {
        title: "Expires",
        dataIndex: "expiresAtUtc",
        key: "expiresAtUtc",
        render: (value, row) =>
          row.revokedAtUtc ? (
            <Badge variant="neutral">Revoked</Badge>
          ) : value ? (
            new Date(value).toLocaleDateString()
          ) : (
            "Never"
          ),
      },
    ];

    return (
      <Table
        columns={topUpColumns}
        dataSource={record.topUps}
        rowKey="paymentId"
        pagination={false}
        size="small"
      />
    );
  };

  const currentPeriod = detail?.periods?.find((period) => period.isCurrent);
  const cycle = detail?.currentCycle;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <Button
          variant="ghost"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/subscription")}
        />
        <h2 style={{ margin: 0 }}>Subscription Detail</h2>
      </div>

      {!id && (
        <Card padding="md" style={{ marginBottom: 24 }}>
          <div style={{ maxWidth: 400 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
              Select Subscription
            </label>
            <SearchSelect
              fetchKey="searchSubscriptionsDetail"
              endpoint="/subscription/search"
              labelKey="description"
              valueKey="subscriptionId"
              placeholder="Search subscription..."
              value={subscriptionId}
              onChange={(value) => {
                setSubscriptionId(value);
                navigate(`/subscription/${value}`, { replace: true });
              }}
            />
          </div>
        </Card>
      )}

      {loading && <Card loading={true} />}

      {!loading && detail && (
        <>
          {/* Debt first — it is the only state on this page that can stop the client's API. */}
          {detail.overdraft?.outstanding > 0 && (
            <Alert
              type={detail.overdraft.isOverdue ? "error" : "warning"}
              showIcon
              style={{ marginBottom: 24 }}
              message={
                detail.overdraft.isOverdue
                  ? `Blocked — ${Number(detail.overdraft.outstanding).toLocaleString()} tokens overdue since ${new Date(
                      detail.overdraft.dueAtUtc
                    ).toLocaleDateString()}`
                  : `${Number(detail.overdraft.outstanding).toLocaleString()} tokens consumed on account`
              }
              description={`Cap ${Number(detail.overdraft.tokenLimit).toLocaleString()} tokens, ${Number(
                detail.overdraft.available
              ).toLocaleString()} still drawable, due ${new Date(
                detail.overdraft.dueAtUtc
              ).toLocaleDateString()}. Recording a paid top-up settles this balance first; only the remainder becomes spendable credit.`}
            />
          )}

          <Card padding="md" style={{ marginBottom: 24 }}>
            <Descriptions
              column={{ xs: 1, sm: 2, md: 3 }}
              size="small"
              labelStyle={{ fontWeight: 500 }}
            >
              <Descriptions.Item label="Client">{detail.clientName}</Descriptions.Item>
              <Descriptions.Item label="Package">{detail.packageName}</Descriptions.Item>
              <Descriptions.Item label="Period Type">
                <Tag>{detail.periodType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Base Tokens">
                {detail.tokenCount?.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Base Price">
                {Number(detail.basePeriodPrice || 0).toFixed(2)} {detail.currencyCode}
              </Descriptions.Item>
              <Descriptions.Item label="Top-Up Terms">
                {detail.topUpTokenCount?.toLocaleString()} /{" "}
                {Number(detail.topUpPrice || 0).toFixed(2)} {detail.currencyCode}
              </Descriptions.Item>
              <Descriptions.Item label="Overdraft Terms">
                {detail.overdraft?.isEnabled
                  ? `${Number(detail.overdraft.tokenLimit || 0).toLocaleString()} tokens / ${
                      detail.overdraft.graceDays
                    } days to settle`
                  : "Disabled — strictly prepaid"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge
                  variant={
                    detail.isArchived ? "neutral" : detail.active ? "success" : "error"
                  }
                >
                  {detail.isArchived ? "Archived" : detail.active ? "Active" : "Inactive"}
                </Badge>
              </Descriptions.Item>
              <Descriptions.Item label="Auto Renew">
                <Badge variant={detail.autoRenew ? "info" : "neutral"}>
                  {detail.autoRenew ? "Yes" : "No"}
                </Badge>
              </Descriptions.Item>
              {detail.nextRenewalDate && (
                <Descriptions.Item label="Next Renewal">
                  {new Date(detail.nextRenewalDate).toLocaleDateString()}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {currentPeriod && (
            <Card title="Current Period" padding="md" style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 32, alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
                    Token Usage
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 600 }}>
                    {currentPeriod.tokenUsed.toLocaleString()}{" "}
                    <span style={{ fontSize: 14, fontWeight: 400, color: "var(--text-secondary)" }}>
                      / {currentPeriod.tokenLimit.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 200, maxWidth: 400 }}>
                  <Progress
                    percent={
                      currentPeriod.tokenLimit > 0
                        ? Math.round((currentPeriod.tokenUsed / currentPeriod.tokenLimit) * 100)
                        : 0
                    }
                    strokeColor={
                      currentPeriod.tokenRemaining === 0
                        ? "var(--color-error)"
                        : currentPeriod.tokenUsed / currentPeriod.tokenLimit >= 0.7
                        ? "var(--color-warning)"
                        : "var(--color-primary)"
                    }
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
                    Remaining
                  </div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 600,
                      color:
                        currentPeriod.tokenRemaining === 0
                          ? "var(--color-error)"
                          : "inherit",
                    }}
                  >
                    {currentPeriod.tokenRemaining.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
                    Billable Amount
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 600 }}>
                    {Number(currentPeriod.totalAmount || 0).toFixed(2)}{" "}
                    {currentPeriod.currencyCode || detail.currencyCode}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {cycle && (
            <Card title="Current Cycle" padding="md" style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 32 }}>
                <CycleBar
                  label="Weekly allowance"
                  used={cycle.weeklyUsed}
                  limit={cycle.weeklyLimit}
                  resetUtc={cycle.weekResetUtc}
                />
                <CycleBar
                  label="Monthly allowance"
                  used={cycle.monthlyUsed}
                  limit={cycle.monthlyLimit}
                  resetUtc={cycle.monthResetUtc}
                />
                <div style={{ minWidth: 160 }}>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
                    Top-up credit
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 600 }}>
                    {Number(detail.wallet?.available || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {detail.wallet?.nextExpiryUtc
                      ? `${Number(detail.wallet.expiringSoon).toLocaleString()} expires ${new Date(
                          detail.wallet.nextExpiryUtc
                        ).toLocaleDateString()}`
                      : "No expiry set"}
                    {detail.wallet?.expired > 0 &&
                      ` · ${Number(detail.wallet.expired).toLocaleString()} lapsed unused`}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 12 }}>
                Bars show plan allowance only. Credit is spent after the allowance runs out, and
                overdraft after that — so a full bar does not mean the API has stopped.
                {cycle.walletUsedInCycle > 0 &&
                  ` ${Number(cycle.walletUsedInCycle).toLocaleString()} credit tokens used this cycle.`}
              </div>
            </Card>
          )}

          <Card title="Payment Periods" padding="sm">
            {detail.periods && detail.periods.length > 0 ? (
              <Table
                columns={tokenColumns}
                dataSource={detail.periods}
                rowKey="paymentId"
                pagination={false}
                size="small"
                expandable={{
                  expandedRowRender,
                  rowExpandable: (row) => row.topUps && row.topUps.length > 0,
                }}
                rowClassName={(row) => (row.isCurrent ? "row-highlight" : "")}
              />
            ) : (
              <Empty description="No payment periods found" />
            )}
          </Card>

          {/* Webhook endpoints are their own module — hidden rather than left to 403. */}
          {canReadWebhooks && (
            <Card title="Webhooks" padding="sm" style={{ marginTop: 24 }}>
              <WebhooksManager subscriptionId={subscriptionId} />
            </Card>
          )}
        </>
      )}

      {!loading && !detail && subscriptionId && <Empty description="Subscription not found" />}
    </div>
  );
};

export default SubscriptionDetail;
